import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useReleasePhoneNumberMutation } from "@/store/rest_api";
import PhoneNumber from "@/model/interface/PhoneNumber";
import { toast } from "sonner";

interface ReleasePhoneNumberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phoneNumber: PhoneNumber | null;
}

export function ReleasePhoneNumberDialog({ open, onOpenChange, phoneNumber }: ReleasePhoneNumberDialogProps) {
  const [releasePhoneNumber, { isLoading }] = useReleasePhoneNumberMutation();

  const handleRelease = async () => {
    if (!phoneNumber) return;

    try {
      await releasePhoneNumber(phoneNumber.id).unwrap();

      toast.success(`Phone number ${phoneNumber.number} has been released`);
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to release phone number");
    }
  };

  if (!phoneNumber) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Release Phone Number?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to release <strong>{phoneNumber.number}</strong>? This action cannot be undone. The
            number will be returned to the pool and you will no longer be charged for it.
            {phoneNumber.callFlow && (
              <div className="mt-2 text-warning">
                This number is currently assigned to the call flow "{phoneNumber.callFlow.name}". Releasing it will
                unassign that flow and turn it off. The flow is kept, so you can assign it to another number later.
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleRelease}
            disabled={isLoading}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isLoading ? "Releasing..." : "Release Number"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
